/**
 * What a change to one exercise actually changes (ai-plan-builder R15): the sets (their number, their
 * types, any prescribed value of any set), the rule that moves them on, or neither (a note, a role).
 * The proposal card names the change by it and shows the part that moved.
 *
 * Every set is compared, field by field, not only the count and the first working set: a load-only
 * change on the second set is a change to the sets.
 *
 * Pure: no React, no i18next.
 */

import type { ExerciseDraft, SetDraft } from '~/entities/plan-draft';

/** The aspects of a prescription that differ between two states of one exercise. */
export interface PrescriptionDiff {
  /** How many sets there are, or what kind each is. */
  readonly structure: boolean;
  readonly volume: boolean;
  readonly load: boolean;
  readonly effort: boolean;
  readonly rest: boolean;
  /** Tempo, speed: prescribed, but not on the line. */
  readonly otherSetValues: boolean;
  readonly progression: boolean;
}

const VOLUME = ['prescribedReps', 'prescribedDurationSeconds', 'prescribedDistanceMeters', 'prescribedRounds'] as const;
const LOAD = ['prescribedWeight', 'prescribedPercent'] as const;
const EFFORT = ['prescribedRpe', 'prescribedRir'] as const;
const OTHER = ['prescribedTempo', 'prescribedSpeed'] as const;

function differs(before: readonly SetDraft[], after: readonly SetDraft[], keys: readonly (keyof SetDraft)[]): boolean {
  return after.some((set, index) => keys.some((key) => set[key] !== before[index]?.[key]));
}

/** A rule map read the same whatever order its keys were written in. */
function ruleText(exercise: ExerciseDraft): string {
  const rules = exercise.progressionRules as Readonly<Record<string, unknown>>;
  return JSON.stringify(
    Object.keys(rules)
      .sort()
      .map((key) => [key, rules[key]]),
  );
}

export function prescriptionDiff(before: ExerciseDraft, after: ExerciseDraft): PrescriptionDiff {
  const structure =
    before.sets.length !== after.sets.length || after.sets.some((set, index) => set.setType !== before.sets[index]?.setType);
  return {
    structure,
    volume: differs(before.sets, after.sets, VOLUME),
    load: differs(before.sets, after.sets, LOAD) || before.prescriptionMode !== after.prescriptionMode,
    effort: differs(before.sets, after.sets, EFFORT),
    rest: differs(before.sets, after.sets, ['prescribedRestTime']),
    otherSetValues: differs(before.sets, after.sets, OTHER),
    progression: ruleText(before) !== ruleText(after),
  };
}

/** What the change is called: the sets moved, only the rule moved, or something else about the exercise. */
export type AlterKind = 'sets' | 'progression' | 'other';

export function alterKind(diff: PrescriptionDiff): AlterKind {
  if (diff.structure || diff.volume || diff.load || diff.effort || diff.rest || diff.otherSetValues) return 'sets';
  return diff.progression ? 'progression' : 'other';
}
